/**
 * Manually derived from: https://shopify.dev/docs/api/admin-graphql/2023-04/objects/Company
 * API Version: 2022-10
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCompanyContactKey,
  ShopifyCompanyContactRoleAssignmentKey,
  ShopifyCompanyContactRoleAssignmentToCompanyKey,
  ShopifyCompanyContactRoleKey,
  ShopifyCompanyContactRoleToCompanyKey,
  ShopifyCompanyContactToCompanyKey,
  ShopifyCompanyKey,
  ShopifyCompanyLocationKey,
  ShopifyCompanyLocationToCompanyKey,
  ShopifyCompanyToCompanyContactKey,
  ShopifyCompanyToCompanyContactRoleKey,
  ShopifyCompanyToContactRoleAssignmentsKey,
  ShopifyCompanyToContactRolesKey,
  ShopifyCompanyToContactsKey,
  ShopifyCompanyToDraftOrdersKey,
  ShopifyCompanyToLocationsKey,
  ShopifyCompanyToOrdersKey,
  ShopifyCompanyToShopKey,
  ShopifyDraftOrderKey,
  ShopifyDraftOrderToCompanyKey,
  ShopifyOrderKey,
  ShopifyOrderToCompanyKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// we added shopify field keys in 2025-04 which changes how the static keys are generated. these are here so that they remain the same
const customerSinceKey = "ModelField-DataModel-Shopify-Company-customerSince";
const customerSinceStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-Company-customerSince-initial";
const externalIdKey = "ModelField-DataModel-Shopify-Company-externalId";
const externalIdStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-Company-externalId-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCompanyKey,
  defaultFilterableApiIdentifiers: ["externalId", "name"],
  "2022-10": {
    name: "Shopify Company",
    graphQLTypename: "Company",
    hasMetafields: true,

    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt", shopifyFieldKey: "created_at" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "updatedAt", shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "name", graphQLFieldKey: "name" } },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "note", graphQLFieldKey: "note" } },
      },
      {
        name: "Contact Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "contactCount" } },
        configuration: { decimals: 0 },
      },
      {
        name: "Order Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orderCount" } },
        configuration: { decimals: 0 },
      },
      {
        name: "Customer Since",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "customerSince" } },
      },
      {
        name: "Default Role",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyToCompanyContactRoleKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "defaultRole", graphQLSelection: "id" },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyContactRoleKey,
        },
      },
      {
        name: "External ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "externalId" } },
      },
      {
        name: "Lifetime Duration",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "lifetimeDuration" } },
      },
      {
        name: "Location Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "locationCount" } },
      },
      {
        name: "Main Contact",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyToCompanyContactKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "mainContact", graphQLSelection: "id" },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyContactKey,
        },
      },
      {
        name: "Total Spent",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "totalSpent",
            graphQLSelection: `
            amount
            currencyCode
        `,
          },
        },
      },
      {
        name: "Contact Roles",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyToContactRolesKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "contact_roles",
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCompanyContactRoleKey,
          inverseFieldKey: ShopifyCompanyContactRoleToCompanyKey,
        },
      },
      {
        name: "Contact Role Assignments",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyToContactRoleAssignmentsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "contact_role_assignments",
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCompanyContactRoleAssignmentKey,
          inverseFieldKey: ShopifyCompanyContactRoleAssignmentToCompanyKey,
        },
      },
      {
        name: "Contacts",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyToContactsKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCompanyContactKey,
          inverseFieldKey: ShopifyCompanyContactToCompanyKey,
        },
      },
      {
        name: "Orders",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyToOrdersKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
          inverseFieldKey: ShopifyOrderToCompanyKey,
        },
      },
      {
        name: "Draft Orders",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyToDraftOrdersKey,
        configuration: {
          relatedModelKey: ShopifyDraftOrderKey,
          inverseFieldKey: ShopifyDraftOrderToCompanyKey,
        },
      },
      // {
      //   name: "Events",
      //   fieldType: FieldType.HasMany,
      //   configuration: {
      //     relatedModelKey: ShopifyEventKey,
      //     inverseFieldKey: ShopifyEventToCompanyKey,
      //   },
      // },
      {
        name: "Locations",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyToLocationsKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCompanyLocationKey,
          inverseFieldKey: ShopifyCompanyLocationToCompanyKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "companies",
      filters: {
        updatedAt: { sortKey: "UPDATED_AT" },
        createdAt: { sortKey: "CREATED_AT" },
      },
      sortableBy: {
        updatedAt: { sortKey: "UPDATED_AT", reverse: true },
        createdAt: { sortKey: "CREATED_AT", reverse: true },
        id: { sortKey: "ID", reverse: false },
        name: { sortKey: "NAME", reverse: false },
        orderCount: { sortKey: "ORDER_COUNT", reverse: true },
        sinceDate: { sortKey: "SINCE_DATE", reverse: true },
        totalSpent: { sortKey: "TOTAL_SPENT", reverse: true },
      },
    },
  },
  "2023-01": {
    hasMetafields: true,
  },

  "2024-04": {
    fields: [
      {
        name: "Contact Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "contactCount", deprecated: true } },
      },
      {
        name: "Order Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orderCount" } },
        isDeleted: true,
      },
      {
        name: "Location Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "locationCount" } },
        isDeleted: true,
      },
      {
        name: "Contacts Count",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "contactsCount",
            graphQLSelection: `
            count
            precision`,
          },
        },
      },
      {
        name: "Orders Count",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "ordersCount",
            graphQLSelection: `
            count
            precision`,
          },
        },
      },
      {
        name: "Locations Count",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "locationsCount",
            graphQLSelection: `
            count
            precision`,
          },
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "Customer Since",
        key: customerSinceKey,
        initialStorageEpochKey: customerSinceStorageEpochKey,
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "customerSince",
            shopifyFieldKey: "customer_since",
            availableInWebhook: [
              "company_contacts/create",
              "company_contacts/update",
              "company_locations/create",
              "company_locations/update",
              "companies/create",
              "companies/update",
            ],
          },
        },
      },
      {
        name: "External ID",
        key: externalIdKey,
        initialStorageEpochKey: externalIdStorageEpochKey,
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "externalId",
            shopifyFieldKey: "external_id",
            availableInWebhook: [
              "company_contacts/create",
              "company_contacts/update",
              "company_locations/create",
              "company_locations/update",
              "companies/create",
              "companies/update",
            ],
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "createdAt",
            shopifyFieldKey: "created_at",
            availableInWebhook: [
              "company_contacts/create",
              "company_contacts/update",
              "company_locations/create",
              "company_locations/update",
              "companies/create",
              "companies/update",
            ],
          },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "updatedAt",
            shopifyFieldKey: "updated_at",
            availableInWebhook: [
              "company_contacts/create",
              "company_contacts/update",
              "company_locations/create",
              "company_locations/update",
              "companies/create",
              "companies/update",
            ],
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
            availableInWebhook: [
              "company_contacts/create",
              "company_contacts/update",
              "company_locations/create",
              "company_locations/update",
              "companies/create",
              "companies/update",
            ],
          },
        },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "note",
            graphQLFieldKey: "note",
            availableInWebhook: [
              "company_contacts/create",
              "company_contacts/update",
              "company_locations/create",
              "company_locations/update",
              "companies/create",
              "companies/update",
            ],
          },
        },
      },
    ],
  },
};

export const ShopifyCompany = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
